import type { ReactNode } from 'react';
import { merge } from '@tragfaehigkeit/core';
import { de } from '../../lib/i18n/de';
import { Badge, type BadgeTone } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SensitiveNote } from './fields/SensitiveNote';

/**
 * Berater-Sicht einer Dimension im Zwei-Ebenen-Modell.
 *
 * Zeigt pro Feld die (read-only) Angabe des Mandanten neben dem editierbaren
 * fachlichen Feld des Beraters. „Angabe übernehmen" kopiert den Mandanten-Wert
 * in die Berater-Ebene. Der wirksame Wert (für Score/Statement) ist der
 * Berater-Wert, sonst die Mandanten-Angabe — berechnet über die pure
 * `merge`-Funktion aus @tragfaehigkeit/core.
 *
 * BEWUSST präsentational + prop-getrieben: keine Store-/DB-/Krypto-Kopplung.
 * Die Verdrahtung an die echte Ebenen-Persistenz erfolgt mit dem Sync-Umbau
 * (Phase 2 des Zwei-Ebenen-Arbeitspakets, siehe docs/Zwei-Ebenen-Modell.md).
 */
export interface BeraterReviewField {
  readonly key: string;
  readonly label: string;
  readonly hint?: string;
  readonly placeholder?: string;
}

export interface BeraterDimensionReviewProps {
  readonly dimensionLabel: string;
  readonly fields: ReadonlyArray<BeraterReviewField>;
  /** Read-only Angaben des Mandanten (Mandant-Ebene). */
  readonly mandantFields: merge.LayerFields;
  /** Editierbare fachliche Eingaben des Beraters (Berater-Ebene). */
  readonly beraterFields: merge.LayerFields;
  readonly reviewStatus: merge.MandantReviewStatus;
  /** Zeitstempel der letzten Mandanten-Bearbeitung (ISO) — optional angezeigt. */
  readonly mandantUpdatedAt?: string | null;
  /** Art.-9-Hinweis für sensible Dimensionen (health/private) — optional. */
  readonly sensitiveNote?: string | undefined;
  /** Optionaler Aktions-Slot im Kartenkopf (z.B. Link zur Einzelansicht). */
  readonly headerAction?: ReactNode;
  readonly onBeraterChange: (key: string, value: string) => void;
  readonly onAdopt: (key: string, value: string) => void;
  readonly onMarkReviewed: () => void;
}

const STATUS_TONE: Record<merge.MandantReviewStatus, BadgeTone> = {
  none: 'neutral',
  unreviewed: 'info',
  stale: 'warning',
  reviewed: 'success',
};

function statusLabel(status: merge.MandantReviewStatus): string {
  switch (status) {
    case 'none':
      return de.beraterReview.statusNone;
    case 'unreviewed':
      return de.beraterReview.statusUnreviewed;
    case 'stale':
      return de.beraterReview.statusStale;
    case 'reviewed':
      return de.beraterReview.statusReviewed;
  }
}

function formatDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString('de-DE');
}

export function BeraterDimensionReview({
  dimensionLabel,
  fields,
  mandantFields,
  beraterFields,
  reviewStatus,
  mandantUpdatedAt,
  sensitiveNote,
  headerAction,
  onBeraterChange,
  onAdopt,
  onMarkReviewed,
}: BeraterDimensionReviewProps) {
  const merged = merge.mergeEffectiveFields(mandantFields, beraterFields);
  const isReviewed = reviewStatus === 'reviewed';

  return (
    <section className="rounded-sm border border-stone-200 bg-white">
      <header className="flex flex-col gap-3 border-b border-stone-200 px-6 py-5 sm:flex-row sm:items-start sm:justify-between md:px-10">
        <div>
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.beraterReview.sectionLabel}
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
            {dimensionLabel}
          </h2>
          {mandantUpdatedAt && (
            <p className="mt-1 font-serif text-xs italic text-stone-500">
              {de.beraterReview.mandantUpdatedAt.replace('{date}', formatDate(mandantUpdatedAt))}
            </p>
          )}
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <Badge tone={STATUS_TONE[reviewStatus]}>{statusLabel(reviewStatus)}</Badge>
          {headerAction}
        </div>
      </header>

      <div className="space-y-8 px-6 py-6 md:px-10">
        {sensitiveNote && <SensitiveNote>{sensitiveNote}</SensitiveNote>}
        {fields.map((f) => {
          const field = merged[f.key];
          const mandantValue = field?.mandantValue ?? '';
          const beraterValue = field?.beraterValue ?? '';
          const source = field?.source ?? 'empty';
          const canAdopt = mandantValue.trim().length > 0 && mandantValue !== beraterValue;
          const beraterFieldId = `berater-${f.key}`;

          return (
            <div key={f.key}>
              <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700">
                {f.label}
              </p>
              {f.hint && <p className="mt-1 font-serif text-sm italic text-stone-500">{f.hint}</p>}

              {/* Mandanten-Angabe (read-only) */}
              <div className="mt-3 rounded-sm border-l-4 border-stone-200 bg-stone-50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
                    {de.beraterReview.mandantPanelLabel}
                  </p>
                  {canAdopt && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      aria-label={de.beraterReview.adoptAriaLabel.replace('{field}', f.label)}
                      onClick={() => {
                        onAdopt(f.key, mandantValue);
                      }}
                    >
                      {de.beraterReview.adoptButton}
                    </Button>
                  )}
                </div>
                <p className="mt-2 whitespace-pre-wrap font-serif text-sm text-stone-700">
                  {mandantValue.trim().length > 0 ? (
                    mandantValue
                  ) : (
                    <span className="italic text-stone-400">{de.beraterReview.mandantEmpty}</span>
                  )}
                </p>
              </div>

              {/* Berater-Eingabe (editierbar) */}
              <label htmlFor={beraterFieldId} className="mt-4 block">
                <span className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
                  {de.beraterReview.beraterFieldLabel}
                </span>
                <textarea
                  id={beraterFieldId}
                  value={beraterValue}
                  onChange={(e) => {
                    onBeraterChange(f.key, e.target.value);
                  }}
                  placeholder={f.placeholder}
                  rows={5}
                  className="mt-2 w-full resize-y rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base leading-relaxed text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
                />
              </label>

              {/* Wirksamer Wert (Provenienz) */}
              <p className="mt-2 font-sans text-[10px] font-medium uppercase tracking-widest text-stone-400">
                {source === 'berater'
                  ? de.beraterReview.effectiveBerater
                  : source === 'mandant'
                    ? de.beraterReview.effectiveMandant
                    : de.beraterReview.effectiveEmpty}
              </p>
            </div>
          );
        })}

        <div className="flex justify-end border-t border-stone-200 pt-6">
          <Button
            type="button"
            variant={isReviewed ? 'secondary' : 'primary'}
            disabled={isReviewed}
            onClick={onMarkReviewed}
          >
            {isReviewed
              ? de.beraterReview.markedReviewedButton
              : `${de.beraterReview.markReviewedButton} →`}
          </Button>
        </div>
      </div>
    </section>
  );
}
